/**
 * SessionList 组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { MessageSquare, Loader2, CheckCircle, Circle, AlertCircle, Plus, Clock, RotateCcw, HelpCircle } from 'lucide-react'
import { useAuthStore } from '@/shared/store/authStore'
import { memoryAdminApi } from '../services/memory-admin-api'
import type { Session } from '../types/memory'

interface SessionListProps {
  userId?: string // undefined 表示获取所有用户的会话（仅 developer/admin）
  projectId?: string
  currentSessionId?: string
  onSessionSelect: (sessionId: string) => void
  onNewSession: () => void
  onReExtract?: (sessionId: string) => void
  canReExtract?: boolean // 是否有重新提取权限（developer/admin）
  statusFilter?: 'all' | 'failed' | 'delayed'
  onSessionsLoaded?: (sessions: Session[]) => void
}

// 状态配置：图标、tooltip 说明
// 状态配置（与后端协议文档保持一致 - 2024-12-23 16:00 状态流转逻辑重构）
// 状态流转：pending → in_progress → completed
//          in_progress → failed → delayed → in_progress（重试）
const STATUS_CONFIG: Record<Session['status'], {
  icon: React.ReactNode
  tooltip: string
}> = {
  pending: {
    icon: <Clock className="w-3 h-3 text-gray-400" />,
    tooltip: '待提取 - 有消息未提取（包括新消息进来）',
  },
  in_progress: {
    icon: <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />,
    tooltip: '提取中 - 正在执行提取任务',
  },
  completed: {
    icon: <CheckCircle className="w-3 h-3 text-green-500" />,
    tooltip: '已完成 - 所有消息已提取',
  },
  failed: {
    icon: <AlertCircle className="w-3 h-3 text-red-500" />,
    tooltip: '提取失败 - 提取过程中发生错误（不可重试）',
  },
  delayed: {
    icon: <Clock className="w-3 h-3 text-orange-500" />,
    tooltip: '延时重试 - 失败后等待重试中',
  },
  skipped: {
    icon: <Circle className="w-3 h-3 text-gray-300" />,
    tooltip: '已跳过 - 没有需要提取的消息',
  },
}

// 未知状态配置（兜底）
const UNKNOWN_STATUS_CONFIG = {
  icon: <HelpCircle className="w-3 h-3 text-yellow-500" />,
  tooltip: '未知状态',
}

// 状态图标组件（带 tooltip）
const StatusIcon = ({ status }: { status: Session['status'] }) => {
  const config = STATUS_CONFIG[status]
  
  // 未知状态：显示 ? 图标并记录日志
  if (!config) {
    console.warn(`[StatusIcon] ⚠️ 未知状态: "${status}", 类型: ${typeof status}`)
    return (
      <div className="relative group flex-shrink-0">
        {UNKNOWN_STATUS_CONFIG.icon}
        {/* Tooltip */}
        <div className="absolute left-0 bottom-full mb-1 px-2 py-1 text-xs rounded shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 bg-slate-800 text-slate-100">
          {`${UNKNOWN_STATUS_CONFIG.tooltip}: ${status}`}
          {/* 小三角 */}
          <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
        </div>
      </div>
    )
  }
  
  return (
    <div className="relative group flex-shrink-0">
      {config.icon}
      {/* Tooltip */}
      <div className="absolute left-0 bottom-full mb-1 px-2 py-1 text-xs rounded shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 bg-slate-800 text-slate-100">
        {config.tooltip}
        {/* 小三角 */}
        <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
      </div>
    </div>
  )
}

// 格式化时间
const formatTime = (dateStr?: string) => {
  if (!dateStr) return ''
  const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
  const date = new Date(utcDateStr)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  
  if (days === 0) {
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  } else if (days === 1) {
    return '昨天'
  } else if (days < 7) {
    return `${days}天前`
  } else {
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
  }
}

// 分组会话
const groupSessions = (sessions: Session[]) => {
  const groups: { [key: string]: Session[] } = {
    '今天': [],
    '昨天': [],
    '更早': [],
  }
  
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
  
  sessions.forEach(session => {
    const dateStr = session.last_message_at
    const utcDateStr = dateStr && !dateStr.endsWith('Z') ? dateStr + 'Z' : dateStr
    const date = utcDateStr ? new Date(utcDateStr) : new Date()
    
    if (date >= today) {
      groups['今天'].push(session)
    } else if (date >= yesterday) {
      groups['昨天'].push(session)
    } else {
      groups['更早'].push(session)
    }
  })
  
  return groups
}

export const SessionList = ({
  userId,
  projectId,
  currentSessionId,
  onSessionSelect,
  onNewSession,
  onReExtract,
  canReExtract = false,
  statusFilter = 'all',
  onSessionsLoaded,
}: SessionListProps) => {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reExtractingSessionId, setReExtractingSessionId] = useState<string | null>(null)

  // 使用 ref 存储回调，避免依赖变化导致重新执行
  const onSessionsLoadedRef = useRef(onSessionsLoaded)
  onSessionsLoadedRef.current = onSessionsLoaded

  // 加载会话列表
  useEffect(() => {
    console.log('[SessionList] useEffect triggered, userId:', userId, 'projectId:', projectId)
    
    const loadSessions = async () => {
      console.log('[SessionList] Loading sessions for userId:', userId || '(all users)')
      setLoading(true)
      setError(null)
      try {
        const response = await memoryAdminApi.getSessions({
          user_id: userId,
          project_id: projectId,
        })
        // 详细日志：检查后端返回的原始数据
        console.log('[SessionList] 📦 后端返回原始数据:', JSON.stringify(response, null, 2))
        console.log('[SessionList] 📊 会话数量:', response.sessions.length)
        // 打印每个会话的状态
        response.sessions.forEach((session, index) => {
          console.log(`[SessionList] 📌 会话[${index}] ID: ${session.session_id}, status: "${session.status}", type: ${typeof session.status}`)
        })
        setSessions(response.sessions)
        onSessionsLoadedRef.current?.(response.sessions)
      } catch (err: unknown) {
        const errorMessage = err instanceof Error 
          ? err.message 
          : (typeof err === 'object' && err !== null ? JSON.stringify(err) : String(err))
        console.error('[SessionList] Failed to load sessions:', errorMessage, err)
        setError('加载会话列表失败')
      } finally {
        setLoading(false)
      }
    }
    
    loadSessions()
    
    // 每 10 秒刷新一次
    const interval = setInterval(loadSessions, 10000)
    return () => clearInterval(interval)
  }, [userId, projectId])

  // 根据筛选条件过滤会话
  const filteredSessions = useMemo(() => {
    if (statusFilter === 'all') return sessions
    return sessions.filter(s => s.status === statusFilter)
  }, [sessions, statusFilter])

  // 分组后的会话
  const groupedSessions = useMemo(() => groupSessions(filteredSessions), [filteredSessions])

  // 重新提取处理
  const handleReExtract = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation()
    if (reExtractingSessionId) return
    
    setReExtractingSessionId(sessionId)
    try {
      const currentUser = useAuthStore.getState().user
      // 用户主键统一用 id
      const currentUserId = currentUser?.id
      if (currentUserId) {
        await memoryAdminApi.reExtractSession(sessionId, currentUserId)
        // 更新本地会话状态为 in_progress（与后端协议一致）
        setSessions(prev => prev.map(s => 
          s.session_id === sessionId ? { ...s, status: 'in_progress' as const } : s
        ))
        onReExtract?.(sessionId)
      }
    } catch (err) {
      console.error('Re-extract failed:', err)
    } finally {
      setReExtractingSessionId(null)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* 新建会话按钮 */}
      <button
        onClick={onNewSession}
        className="flex items-center gap-2 px-3 py-2 mx-2 mb-2 rounded-lg transition-colors bg-blue-500 hover:bg-blue-600 text-white"
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm font-medium">新建会话</span>
      </button>

      {/* 会话列表 */}
      <div className="flex-1 overflow-y-auto px-2">
        {loading && sessions.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-sm text-slate-400">
            {error}
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">
            暂无会话，点击上方按钮开始
          </div>
        ) : (
          Object.entries(groupedSessions).map(([group, items]) => {
            if (items.length === 0) return null
            return (
              <div key={group} className="mb-4">
                {/* 分组标题 */}
                <div className="text-xs font-medium px-2 py-1 mb-1 text-slate-400">
                  📅 {group} ({items.length})
                </div>
                
                {/* 会话项 */}
                {items.map(session => {
                  const canRetry = canReExtract && (session.status === 'failed' || session.status === 'delayed')
                  const isRetrying = reExtractingSessionId === session.session_id
                  
                  return (
                    <div
                      key={session.session_id}
                      className="relative group"
                    >
                      <button
                        onClick={() => onSessionSelect(session.session_id)}
                        className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors ${
                          session.session_id === currentSessionId
                            ? 'bg-blue-500/20 border-l-3 border-l-blue-500'
                            : 'border-l-3 border-l-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <StatusIcon status={session.status} />
                          <div className="flex-1 min-w-0">
                            {/* 摘要 */}
                            <div className="text-sm font-medium truncate text-slate-100">
                              {session.title || `会话 ${session.session_id.slice(0, 8)}`}
                            </div>
                            {/* 账号信息 */}
                            <div className="text-xs mt-1 px-1.5 py-0.5 rounded truncate inline-flex items-center gap-1 max-w-full bg-blue-500/15 text-blue-400">
                              <span>👤</span>
                              <span className="truncate font-medium">{session.user_id || userId}</span>
                            </div>
                            {/* 元信息 */}
                            <div className="text-xs mt-1 flex items-center gap-2 text-slate-400">
                              <span className="truncate">{session.session_id.slice(0, 8)}</span>
                              <span>|</span>
                              <span>{formatTime(session.last_message_at)}</span>
                              <span>|</span>
                              <span className="flex items-center gap-0.5">
                                <MessageSquare className="w-3 h-3" />
                                {session.message_count}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                      
                      {/* 重新提取按钮 */}
                      {canRetry && (
                        <button
                          onClick={(e) => handleReExtract(e, session.session_id)}
                          disabled={isRetrying}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 bg-yellow-500/20 text-yellow-400 disabled:opacity-50"
                          title="重新提取"
                        >
                          {isRetrying ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default SessionList

